import React, { useState } from 'react'

import { ArrowUpRight, ChevronDown, ChevronRight } from 'lucide-react'
import { useShallow } from 'zustand/react/shallow'

import { environment, site } from 'utils/server-data'
import { getSiteUrl } from 'utils/site'
import { cn } from 'utils/utils'

import { Button } from 'components/ui/button'
import { ScrollArea } from 'components/ui/scroll-area'

import { PlaybookRule } from '../../../../schemas/playbook'
import { useAccessTokenContext } from '../../providers/AccessTokenProvider'
import useAppStore from '../../stores/store'

const RULE_NAME_SEARCH_PARAM_KEY = 'rule'
const ACTIVE_PLAYBOOK_SEARCH_PARAM_KEY = 'playbook_id'

interface RuleDetailsProps {
  rule: PlaybookRule
  headerContent?: React.ReactNode
}

export const RuleDetails: React.FC<RuleDetailsProps> = ({
  rule,
  headerContent,
}) => {
  const { playbook } = useAppStore(
    useShallow((s) => ({
      playbook: s.playbook,
    }))
  )
  const { userInfo } = useAccessTokenContext()
  const [open, setOpen] = useState(false)
  const acceptableDeviations = rule.acceptableDeviations?.join('\n\n')
  const unacceptableDeviations = rule.unacceptableDeviations?.join('\n\n')

  const baseUrl = `${getSiteUrl(site, environment)}/settings/playbooks`
  const searchParams = new URLSearchParams({
    [ACTIVE_PLAYBOOK_SEARCH_PARAM_KEY]: playbook?.id ?? '',
    [RULE_NAME_SEARCH_PARAM_KEY]: rule.name,
  })
  const urlWithSearchParams = `${baseUrl}?${searchParams.toString()}`

  return (
    <div className="flex flex-col rounded-md">
      <Button
        onClick={() => setOpen(!open)}
        className={cn(
          'flex h-fit w-full items-center justify-between gap-2 bg-accent py-2 pl-3 pr-2.5 transition hover:bg-accent',
          open && 'rounded-b-none'
        )}
        variant="ghost"
      >
        {headerContent}
        {open ? (
          <ChevronDown className="size-3" />
        ) : (
          <ChevronRight className="size-3" />
        )}
      </Button>
      <ScrollArea>
        {open && (
          <div className="flex flex-col gap-4 rounded-b-md bg-accent px-3 py-2">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-primary">
                Standard position
              </span>
              <span className="whitespace-pre-wrap text-xs text-muted">
                {rule.standardPosition || 'None'}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-primary">
                Fallback positions
              </span>
              <span className="whitespace-pre-wrap text-xs text-muted">
                {acceptableDeviations || 'None'}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-primary">
                Unacceptable positions
              </span>
              <span className="whitespace-pre-wrap text-xs text-muted">
                {unacceptableDeviations || 'None'}
              </span>
            </div>
            {userInfo?.isPlaybookAdminUser && (
              <Button
                variant="outline"
                size="sm"
                className="flex w-fit gap-1 bg-primary"
                onClick={() => {
                  window.open(urlWithSearchParams, '_blank')
                }}
              >
                <span className="text-xs">Edit rule</span>
                <ArrowUpRight className="size-3" />
              </Button>
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  )
}
